import * as React from "react";
import { useMutation } from "convex/react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/convexApi";
import type { RecoveryResult } from "./AccountRecoveryCard";

type Profile = RecoveryResult["userProfiles"][number];

export function CustomerProfileEditDialog({
  open,
  onOpenChange,
  authUserId,
  loginEmail,
  profile,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  authUserId?: string;
  loginEmail: string;
  profile: Profile;
  onSaved: () => Promise<void>;
}) {
  const [saving, setSaving] = React.useState(false);
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [company, setCompany] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [accountingEmail, setAccountingEmail] = React.useState("");
  const [orderingEmail, setOrderingEmail] = React.useState("");
  const [truckingEmail, setTruckingEmail] = React.useState("");
  const updateProfile = useMutation(api.accountRecovery.updateCustomerProfile);

  React.useEffect(() => {
    if (!open) return;
    setFirstName(profile.firstName ?? "");
    setLastName(profile.lastName ?? "");
    setCompany(profile.company ?? "");
    setPhone(profile.mainPhoneNumber ?? "");
    setAccountingEmail(profile.accountingEmail ?? "");
    setOrderingEmail(profile.orderingEmail ?? "");
    setTruckingEmail(profile.truckingEmail ?? "");
  }, [open, profile]);

  const hasName = Boolean(firstName.trim() || lastName.trim());

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!authUserId) {
      toast.error("Your admin session is not ready. Refresh and try again.");
      return;
    }
    if (!hasName) {
      toast.error("Enter a first name, a last name, or both");
      return;
    }
    if (profile.updatedAt === null) {
      toast.error("This profile has no update timestamp. Refresh before editing.");
      return;
    }

    setSaving(true);
    try {
      await updateProfile({
        authUserId,
        profileId: profile._id as any,
        expectedUpdatedAt: profile.updatedAt,
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        company: company.trim() || null,
        mainPhoneNumber: phone.trim() || null,
        accountingEmail: accountingEmail.trim() || null,
        orderingEmail: orderingEmail.trim() || null,
        truckingEmail: truckingEmail.trim() || null,
      });
      toast.success(`Customer profile updated for ${loginEmail}`);
      await onSaved();
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Failed to update customer profile",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!saving) onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit customer profile</DialogTitle>
          <DialogDescription>
            Update customer and email-routing information. The login email is
            read-only because changing it requires a separate ownership and
            verification workflow.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-5" onSubmit={save}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="recovery-first-name">First name</Label>
              <Input
                id="recovery-first-name"
                value={firstName}
                maxLength={100}
                disabled={saving}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="recovery-last-name">Last name</Label>
              <Input
                id="recovery-last-name"
                value={lastName}
                maxLength={100}
                disabled={saving}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="recovery-company">Company</Label>
              <Input
                id="recovery-company"
                value={company}
                maxLength={200}
                disabled={saving}
                onChange={(event) => setCompany(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="recovery-phone">Main phone</Label>
              <Input
                id="recovery-phone"
                type="tel"
                value={phone}
                maxLength={50}
                disabled={saving}
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="recovery-login-email">Login email</Label>
            <Input id="recovery-login-email" value={loginEmail} disabled />
            <p className="text-xs text-muted-foreground">
              Used for authentication and verification. It cannot be changed
              from this profile form.
            </p>
          </div>

          <fieldset className="space-y-3 rounded-lg border p-4">
            <legend className="px-1 text-sm font-medium">
              Contact email routing
            </legend>
            <p className="text-xs text-muted-foreground">
              Leave a field empty to use the primary customer email.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="recovery-accounting-email">Accounting</Label>
                <Input
                  id="recovery-accounting-email"
                  type="email"
                  value={accountingEmail}
                  disabled={saving}
                  onChange={(event) => setAccountingEmail(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="recovery-ordering-email">Ordering</Label>
                <Input
                  id="recovery-ordering-email"
                  type="email"
                  value={orderingEmail}
                  disabled={saving}
                  onChange={(event) => setOrderingEmail(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="recovery-trucking-email">Trucking</Label>
                <Input
                  id="recovery-trucking-email"
                  type="email"
                  value={truckingEmail}
                  disabled={saving}
                  onChange={(event) => setTruckingEmail(event.target.value)}
                />
              </div>
            </div>
          </fieldset>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !hasName || profile.updatedAt === null}
            >
              {saving ? <Loader2 className="animate-spin" /> : <Save />}
              {saving ? "Saving..." : "Save profile"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
